import { ACTIVITY_REQUEST_LEGS, REGISTRATION_MODES } from '@/constants/activity-requests.constants'
import { REQUESTED_LEG_LABELS } from '@/constants/ds-attendance.constants'
import type {
  DSAttendanceHealthUnitFilterOption,
  DSAttendanceScheduleFilterOption
} from '@/Network/Services/DsAttendanceService'
import type { ShiftResponse } from '@/Network/Types/Responses/ShiftResponses'
import type { ActivityRequestLeg, RegistrationMode } from '@/types/activity-requests.types'
import { formatDateInOffset } from '@/utils/date'

import type { ActivityRequestFilters } from '../composables/useActivityRequestFilters'
import { registrationModeLabel } from './activity-request-format'

/**
 * Chip de filtro — estruturalmente compatível com o `ActivityRequestChip` do `ActiveFilters.vue`.
 * Declarado aqui porque importar tipo nomeado de SFC num `.ts` quebra o ts-loader do build
 * (mesmo motivo de `PostStation/filterTags.ts`).
 */
export interface ActivityRequestChip {
  id: string | number
  label: string
  type: string
  value?: string | number
}

export interface FilterOption {
  label: string
  value: string
}

const byLabel = (a: FilterOption, b: FilterOption): number =>
  a.label.localeCompare(b.label, 'pt-BR')

/** `HRBZ - Hospital Regional…` → `HRBZ`; sem " - " o título inteiro. */
export function unitPrefix(title: string): string {
  const [head] = title.split(' - ')
  return head?.trim() || title
}

export function buildUnitOptions(units: DSAttendanceHealthUnitFilterOption[]): FilterOption[] {
  return units.map((unit) => ({ label: unit.title, value: String(unit.value) })).sort(byLabel)
}

/**
 * M1 decisão 1 — Escala independente: sem unidade, todas as do escopo; com unidade, só as dela.
 * A que já está marcada fica na lista mesmo fora do recorte, para poder ser desmarcada.
 */
export function buildScheduleOptions(
  schedules: DSAttendanceScheduleFilterOption[],
  units: DSAttendanceHealthUnitFilterOption[],
  selectedUnitIds: number[],
  selectedScheduleIds: number[]
): FilterOption[] {
  const prefixByUnit = new Map(units.map((unit) => [unit.value, unitPrefix(unit.title)]))
  return schedules
    .filter(
      (schedule) =>
        !selectedUnitIds.length ||
        selectedUnitIds.includes(schedule.healthUnitId) ||
        selectedScheduleIds.includes(schedule.value)
    )
    .map((schedule) => ({
      label: `${prefixByUnit.get(schedule.healthUnitId) ?? '—'} · ${schedule.title}`,
      value: String(schedule.value)
    }))
    .sort(byLabel)
}

/**
 * Turno independente (M1 decisão 1). Estreita por escala escolhida; sem escala, pela unidade;
 * sem nenhum dos dois, todos os turnos das escalas do escopo da pessoa. Turno de escala fora
 * do escopo nunca aparece (a pessoa não o alcança).
 */
export function buildShiftOptions(
  shifts: ShiftResponse[],
  schedules: DSAttendanceScheduleFilterOption[],
  units: DSAttendanceHealthUnitFilterOption[],
  selectedUnitIds: number[],
  selectedScheduleIds: number[],
  selectedShiftIds: number[]
): FilterOption[] {
  const prefixByUnit = new Map(units.map((unit) => [unit.value, unitPrefix(unit.title)]))
  const scheduleById = new Map(schedules.map((schedule) => [schedule.value, schedule]))

  const allowedSchedule = (scheduleId: number): boolean => {
    const schedule = scheduleById.get(scheduleId)
    if (!schedule) return false
    if (selectedScheduleIds.length) return selectedScheduleIds.includes(scheduleId)
    if (selectedUnitIds.length) return selectedUnitIds.includes(schedule.healthUnitId)
    return true
  }

  return shifts
    .filter((shift) => {
      if (!shift.schedule || !scheduleById.has(shift.schedule.id)) return false
      if (selectedShiftIds.includes(shift.id)) return true
      return shift.isActive !== false && allowedSchedule(shift.schedule.id)
    })
    .map((shift) => {
      const schedule = scheduleById.get(shift.schedule.id)!
      const prefix = prefixByUnit.get(schedule.healthUnitId) ?? '—'
      return { label: `${prefix} · ${schedule.title} · ${shift.name}`, value: String(shift.id) }
    })
    .sort(byLabel)
}

export const REGISTRATION_MODE_OPTIONS: FilterOption[] = REGISTRATION_MODES.map((mode) => ({
  label: registrationModeLabel(mode),
  value: mode
}))

/** Mesmos rótulos do card ("Check-in e check-out", nunca "Ambas") — DEV-QA do PR #2839. */
const legLabel = (leg: ActivityRequestLeg): string => REQUESTED_LEG_LABELS[leg] ?? leg

export const REQUESTED_LEG_OPTIONS: FilterOption[] = ACTIVITY_REQUEST_LEGS.map((leg) => ({
  label: legLabel(leg),
  value: leg
}))

export interface ChipOptionSources {
  units: FilterOption[]
  schedules: FilterOption[]
  shifts: FilterOption[]
}

const labelOf = (options: FilterOption[], value: string): string =>
  options.find((option) => option.value === value)?.label ?? `#${value}`

const yesNo = (value: boolean): string => (value ? 'Sim' : 'Não')

const brDate = (day: string): string => day.split('-').reverse().join('/')

const listChips = (
  filters: ActivityRequestFilters,
  sources: ChipOptionSources
): ActivityRequestChip[] => [
  ...filters.healthUnitIds.map((id) => ({
    id: `unit-${id}`,
    label: `Unidade: ${labelOf(sources.units, String(id))}`,
    type: 'unit',
    value: id
  })),
  ...filters.operationalGroups.map((group) => ({
    id: `group-${group}`,
    label: `Grupo: ${group}`,
    type: 'group',
    value: group
  })),
  ...filters.scheduleIds.map((id) => ({
    id: `schedule-${id}`,
    label: `Escala: ${labelOf(sources.schedules, String(id))}`,
    type: 'schedule',
    value: id
  })),
  ...filters.shiftIds.map((id) => ({
    id: `shift-${id}`,
    label: `Turno: ${labelOf(sources.shifts, String(id))}`,
    type: 'shift',
    value: id
  })),
  ...filters.registrationModes.map((mode) => ({
    id: `registrationMode-${mode}`,
    label: `Modalidade: ${registrationModeLabel(mode)}`,
    type: 'registrationMode',
    value: mode
  })),
  ...filters.requestedLeg.map((leg) => ({
    id: `leg-${leg}`,
    label: `Perna: ${legLabel(leg)}`,
    type: 'leg',
    value: leg
  }))
]

const stationDateLabel = (start: string | null, end: string | null): string => {
  const from = brDate(start ?? end ?? '')
  return end && end !== start ? `${from} – ${brDate(end)}` : from
}

const deadlineLabel = (from: string | null, to: string | null): string => {
  const first = formatDateInOffset(from ?? to ?? '') ?? ''
  return to ? `${first} – ${formatDateInOffset(to) ?? ''}` : first
}

const rangeChips = (filters: ActivityRequestFilters): ActivityRequestChip[] => {
  const chips: ActivityRequestChip[] = []
  if (filters.startDate || filters.endDate)
    chips.push({
      id: 'stationDate',
      label: `Plantão: ${stationDateLabel(filters.startDate, filters.endDate)}`,
      type: 'stationDate'
    })
  if (filters.deadlineFrom || filters.deadlineTo)
    chips.push({
      id: 'deadline',
      label: `Prazo: ${deadlineLabel(filters.deadlineFrom, filters.deadlineTo)}`,
      type: 'deadline'
    })
  return chips
}

const TRI_STATE_CHIPS: Array<{
  key: 'hasAntecipe' | 'hasInCash' | 'reopened'
  id: string
  label: string
}> = [
  { key: 'hasAntecipe', id: 'antecipe', label: 'Antecipe' },
  { key: 'hasInCash', id: 'inCash', label: 'À vista' },
  { key: 'reopened', id: 'reopened', label: 'Reaberta' }
]

const triStateChips = (filters: ActivityRequestFilters): ActivityRequestChip[] =>
  TRI_STATE_CHIPS.flatMap(({ key, id, label }) => {
    const value = filters[key]
    return value === null ? [] : [{ id, label: `${label}: ${yesNo(value)}`, type: id }]
  })

/** Todo filtro ativo — inclusive os de "Mais filtros" — aparece como chip removível. */
export function buildActivityRequestChips(
  filters: ActivityRequestFilters,
  sources: ChipOptionSources
): ActivityRequestChip[] {
  const search = filters.search.trim()
  const searchChips: ActivityRequestChip[] = search
    ? [{ id: 'search', label: `Busca: "${search}"`, type: 'search' }]
    : []
  return [
    ...searchChips,
    ...listChips(filters, sources),
    ...rangeChips(filters),
    ...triStateChips(filters)
  ]
}

type ChipRemoval = (
  filters: ActivityRequestFilters,
  value: string | number | undefined
) => Partial<ActivityRequestFilters>

const CHIP_REMOVALS: Record<string, ChipRemoval> = {
  search: () => ({ search: '' }),
  unit: (f, v) => ({ healthUnitIds: f.healthUnitIds.filter((id) => id !== v) }),
  group: (f, v) => ({ operationalGroups: f.operationalGroups.filter((g) => g !== v) }),
  schedule: (f, v) => ({ scheduleIds: f.scheduleIds.filter((id) => id !== v) }),
  shift: (f, v) => ({ shiftIds: f.shiftIds.filter((id) => id !== v) }),
  registrationMode: (f, v) => ({
    registrationModes: f.registrationModes.filter((mode): mode is RegistrationMode => mode !== v)
  }),
  leg: (f, v) => ({ requestedLeg: f.requestedLeg.filter((leg) => leg !== v) }),
  stationDate: () => ({ startDate: null, endDate: null }),
  deadline: () => ({ deadlineFrom: null, deadlineTo: null }),
  antecipe: () => ({ hasAntecipe: null }),
  inCash: () => ({ hasInCash: null }),
  reopened: () => ({ reopened: null })
}

export function chipRemovalPatch(
  filters: ActivityRequestFilters,
  chip: ActivityRequestChip
): Partial<ActivityRequestFilters> {
  return CHIP_REMOVALS[chip.type]?.(filters, chip.value) ?? {}
}
